// Public legal pages — /privacy and /terms.
//
// App Store Connect requires the privacy policy at a public URL, and both
// documents are linked from the app's sign-in screen and Settings. The copy
// here MUST stay in sync with the in-app versions
// (App/.../Views/Settings/LegalViews.swift) — update both together.

type Section = { title: string; body: string };

const LAST_UPDATED = "September 13, 2026";

const PRIVACY_SECTIONS: Section[] = [
  {
    title: "Who we are",
    body: "Story Stamp lets you attach stories — photos, video, and words — to physical objects using QR stickers. This policy explains exactly what information we collect, why, who can see it, and how to delete it. The short version: your stories belong to you, you control who sees them, we don't run ads, and we never sell your data.",
  },
  {
    title: "Account information",
    body: "When you sign in with Apple or Google we receive your name and an email address. If you use Apple's “Hide My Email,” we only ever see the private relay address Apple creates — never your real one — and we don't touch it. You can optionally add a separate contact email in Settings → Account.",
  },
  {
    title: "Keeper contact details",
    body: "You may designate a Keeper — a trusted person we can contact so your stories are cared for if something happens to you. We store the email address (and optional phone number) you provide, use it only for that purpose, and ask that you get your Keeper's permission before adding them.",
  },
  {
    title: "Your stories",
    body: "We store what you attach to each stamp: a title, category, optional written story, optional estimated value, up to three photos, and an optional video, along with when it was created and the sticker code it's linked to.",
  },
  {
    title: "Circle, comments, and hearts",
    body: "If you invite people to your Circle, we store those connections, the invitations you create, and the comments and hearts exchanged on shared stories. Comments are private: only the story's owner and the comment's own author can read them — never other members, and never the public.",
  },
  {
    title: "Who can see what",
    body: "By default, a story can be read by anyone who physically scans its stamp (they need the sticker in hand — stories are not searchable or listed anywhere). You can turn this off per object with Privacy Mode. Stories are shared with your Circle only as you choose: everyone, specific people, or nobody. Drafts are visible only to you.",
  },
  {
    title: "Usage analytics",
    body: "We use Google Firebase Analytics to understand basic app usage (which screens are used, roughly how often). This data is not used to track you across other apps or websites, and we don't use advertising SDKs of any kind.",
  },
  {
    title: "Where your data lives",
    body: "Your data is processed and stored by Google Firebase (Google Cloud) on servers in the United States. Media is encrypted in transit and at rest. Sticker purchases happen on our Shopify-powered store — payment details go to Shopify and its payment processors; we never see or store your card numbers.",
  },
  {
    title: "How long we keep it",
    body: "Your content stays as long as your account is in good standing (see our Terms of Service for what happens if an account lapses — content may be removed starting two years after a required payment stops). Drafts expire after 7 days. Deleting a story removes it and its media from our servers right away.",
  },
  {
    title: "Deleting your account",
    body: "Settings → Account → Delete Account removes everything, immediately and automatically: your profile, your stories and their photos and videos, your Circle connections in both directions, invitations, comments and reading history, and your sign-in. If you signed in with Apple, we also revoke the Sign in with Apple connection.",
  },
  {
    title: "Children",
    body: "Story Stamp is not directed at children under 13, and we don't knowingly collect information from them.",
  },
  {
    title: "Changes to this policy",
    body: "If we make material changes we'll tell you in the app or by email before they take effect.",
  },
  {
    title: "Contact",
    body: "Questions or requests about your data? Email support@story-stamp.com.",
  },
];

const TERMS_SECTIONS: Section[] = [
  {
    title: "Agreeing to these terms",
    body: "By creating an account or using Story Stamp, you agree to these Terms of Service and our Privacy Policy. If you don't agree, please don't use the service. You must be at least 13 years old.",
  },
  {
    title: "What Story Stamp is",
    body: "Story Stamp pairs physical QR stickers (“stamps”) with a digital service for recording and sharing the stories behind your objects. The stickers are a physical product; the app, story hosting, and sharing features are a digital service. These terms cover both.",
  },
  {
    title: "Stickers and purchases",
    body: "Stamps are sold through our online store, powered by Shopify. Prices, shipping, and returns are as stated in the store at the time of purchase. Refunds are available within 30 days for unused sticker packs. Physical sticker purchases are separate from the digital storage plan described below.",
  },
  {
    title: "Applying stickers — your responsibility",
    body: "Stamps use adhesive. Adhesives can mark, discolor, or damage some surfaces and finishes — especially antiques, unsealed wood, leather, paper, textiles, and delicate coatings — and removal may leave residue or cause damage. Test in an inconspicuous spot first, and apply at your own risk. To the fullest extent permitted by law, Story Stamp is not responsible for any damage to your objects or other property arising from applying, wearing, weathering, or removing a sticker. We make no promise that a sticker will adhere to, survive on, or scan from any particular surface or in any particular conditions.",
  },
  {
    title: "The storage plan (billing, trial, and no refunds)",
    body: "Activating a new stamp requires an active Story Stamp storage plan: $14.99 per year (or the local equivalent shown at purchase), billed through your Apple App Store or Google Play account. Your first 30 days are free; after the trial, the plan auto-renews yearly until you cancel in your device's subscription settings, at least 24 hours before renewal to avoid the next charge. The plan covers hosting for your stories, photos, and videos across all your devices. ALL SALES ARE FINAL: to the fullest extent permitted by law, we do not offer refunds or credits for any billing period, including unused time after cancellation. Refund requests are handled exclusively by Apple or Google under their own policies, and canceling stops future charges but does not refund past ones. If your plan lapses, your published stories remain viewable and scannable, but activating new stamps is paused until you resubscribe — and your content may be permanently deleted beginning two years after your last payment (we will make reasonable efforts to notify you at your account email first). Backing up your own copies of irreplaceable photos and videos is always a good idea.",
  },
  {
    title: "Your content",
    body: "You own the stories, photos, and videos you create. You grant us a limited, worldwide license to host, store, reproduce, and display that content solely to operate the service — showing your stories to you and to the audiences you choose. The license ends when you delete the content or your account, except for brief backup retention.",
  },
  {
    title: "Sharing and visibility",
    body: "You control who sees each story: anyone who physically scans its stamp (unless you turn that off with Privacy Mode), your Circle, specific people, or nobody. Anyone you allow to view a story may be able to screenshot or copy it — share thoughtfully. If you designate a Keeper, you confirm you have that person's permission to give us their contact details.",
  },
  {
    title: "Acceptable use",
    body: "Don't upload content that is illegal, infringing, hateful, sexually exploitative, or harassing; don't use Story Stamp to deceive, spam, or harm others; don't attempt to break, probe, or overload the service. We have zero tolerance for objectionable content and may remove content or suspend accounts that violate these terms.",
  },
  {
    title: "Other people's content",
    body: "Stories shared with you belong to the people who made them. Don't republish someone else's stories, photos, or videos outside Story Stamp without their permission.",
  },
  {
    title: "Service changes and availability",
    body: "We're a small company building something built to last, but we can't promise uninterrupted availability, and features may change. If we ever discontinue the service, we will make reasonable efforts to give you notice and a way to export your stories first.",
  },
  {
    title: "Disclaimers",
    body: "Story Stamp is provided “as is” and “as available,” without warranties of any kind, express or implied, including fitness for a particular purpose. This applies to the app, the web viewer, and the physical stickers alike, except where law requires otherwise.",
  },
  {
    title: "Limitation of liability",
    body: "To the fullest extent permitted by law, Story Stamp and its founders will not be liable for indirect, incidental, special, consequential, or punitive damages — including damage to objects or property, loss of data, or loss of sentimental value — and our total liability for any claim is limited to the amount you paid us in the 12 months before the claim arose.",
  },
  {
    title: "Indemnification",
    body: "You agree to defend us against claims arising from content you upload or your violation of these terms.",
  },
  {
    title: "Termination",
    body: "You can stop using Story Stamp and delete your account at any time in Settings. We may suspend or terminate accounts that violate these terms; where practical, we'll warn you first.",
  },
  {
    title: "Governing law",
    body: "These terms are governed by the laws of the United States and the state in which our company is registered, without regard to conflict-of-law principles.",
  },
  {
    title: "Changes to these terms",
    body: "We may update these terms. For material changes, we'll notify you in the app or by email before they take effect; continuing to use Story Stamp after that means you accept the updated terms.",
  },
  {
    title: "Contact",
    body: "Questions? Email support@story-stamp.com.",
  },
];

function LegalPage({ title, sections }: { title: string; sections: Section[] }) {
  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <h1 className="text-3xl font-semibold tracking-tight text-textPrimary mb-8">
        {title}
      </h1>
      <div className="space-y-7">
        {sections.map((s) => (
          <section key={s.title}>
            <h2 className="text-lg font-semibold text-textPrimary mb-1.5">
              {s.title}
            </h2>
            <p className="text-textSecondary leading-relaxed">{s.body}</p>
          </section>
        ))}
      </div>
      <p className="mt-10 text-sm text-textSecondary/70">
        Last updated: {LAST_UPDATED}
      </p>
    </div>
  );
}

export function Privacy() {
  return <LegalPage title="Privacy Policy" sections={PRIVACY_SECTIONS} />;
}

export function Terms() {
  return <LegalPage title="Terms of Service" sections={TERMS_SECTIONS} />;
}
